import MicOn from '@/assets/images/Mic2.svg';
import MicMute from '@/assets/images/MicOff2.svg';
import Video from '@/assets/images/Video2.svg';
import VideoOff from '@/assets/images/VideoOff2.svg';
import Loader from '@/components/common/Loader/Loader';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { showToast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { Role } from '@/Types/gql/graphql';
import { authState } from '@/vars/auth';
import { useReactiveVar } from '@apollo/client';
import ZoomVideo from '@zoom/videosdk';
import dayjs from 'dayjs';
import { truncate } from 'lodash';
import { Calendar, Camera, Clock, Mic, Volume2 } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { activeMediaDeviceVar, sessionVar } from '../meetingVar';

interface PreSessionProps {
  isVideoOn: boolean;
  isAudioOn: boolean;
  setIsVideoOn: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAudioOn: React.Dispatch<React.SetStateAction<boolean>>;
  handleJoin: () => void;
  loading: boolean;
}

const localAudio = ZoomVideo.createLocalAudioTrack();
const localVideo = ZoomVideo.createLocalVideoTrack();
let allDevices: MediaDeviceInfo[];

const PreSession: React.FC<PreSessionProps> = ({
  isAudioOn,
  isVideoOn,
  setIsAudioOn,
  setIsVideoOn,
  handleJoin,
  loading: joinLoading,
}) => {
  const sessionData = useReactiveVar(sessionVar);
  const { userDetails } = useReactiveVar(authState);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { camera, mic, speaker } = useReactiveVar(activeMediaDeviceVar);

  const deduplicateDevices = (devices: MediaDeviceInfo[]) => {
    const seenGroups = new Set();
    return devices.filter((device) => {
      if (seenGroups.has(device.groupId)) return false;
      seenGroups.add(device.groupId);
      return true;
    });
  };

  const mountDevices = async () => {
    allDevices = await ZoomVideo.getDevices();

    const cameraDevicesRaw = allDevices.filter((device) => device.kind === 'videoinput');
    const micDevicesRaw = allDevices.filter((device) => device.kind === 'audioinput');
    const audioDevicesRaw = allDevices.filter((device) => device.kind === 'audiooutput');

    const cameraDevices = deduplicateDevices(cameraDevicesRaw);
    const micDevices = deduplicateDevices(micDevicesRaw);
    const audioDevices = deduplicateDevices(audioDevicesRaw);

    activeMediaDeviceVar({
      mic: micDevices[0]?.deviceId || '',
      speaker: audioDevices[0]?.deviceId || '',
      camera: cameraDevices[0]?.deviceId || '',
    });
    localVideo.switchCamera(cameraDevices[0]?.deviceId);
    // Optional: Update global allDevices to only have deduplicated ones
    allDevices = [...cameraDevices, ...micDevices, ...audioDevices];
  };

  const audioInputDevices = allDevices
    ?.filter((device) => device.kind === 'audioinput')
    .map((item) => ({
      text: item.label,
      value: item.deviceId,
    }));

  const handleMicChange = async (value: string) => {
    activeMediaDeviceVar({
      ...activeMediaDeviceVar(),
      mic: value,
    });
  };

  const audioOutputDevices = allDevices
    ?.filter((device) => device.kind === 'audiooutput')
    .map((item) => ({
      text: item.label,
      value: item.deviceId,
    }));

  const handleSpeakerChange = (value: string) => {
    activeMediaDeviceVar({
      ...activeMediaDeviceVar(),
      speaker: value,
    });
  };

  const cameraDevices = allDevices
    ?.filter((device) => device.kind === 'videoinput')
    .map((item) => ({
      text: item.label,
      value: item.deviceId,
    }));

  const handleCameraChange = async (value: string) => {
    try {
      await localVideo.switchCamera(value);
      activeMediaDeviceVar({
        ...activeMediaDeviceVar(),
        camera: value,
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const getDevices = async () => {
      try {
        await mountDevices();
      } catch (error) {
        console.error('Error getting devices:', error);
      }
    };
    getDevices();
  }, []);

  useEffect(() => {
    const enableDefaultDevices = async () => {
      setLoading(true);
      try {
        await mountDevices();
        if (videoRef.current) {
          await localVideo
            .start(videoRef.current)
            .then(() => {
              setIsVideoOn(true);
            })
            .catch(async (err) => {
              console.log('err', err);
              if (err === 'NotAllowedError: Permission denied') {
                await navigator.mediaDevices.getUserMedia({ video: true });
              }
            });
        }
        await localAudio
          .start()
          .then(() => {
            setIsAudioOn(true);
          })
          .catch(async (err) => {
            console.log('err', err);
            if (err === 'NotAllowedError: Permission denied') {
              await navigator.mediaDevices.getUserMedia({ audio: true });
            }
          });
        setLoading(false);
      } catch (error) {
        console.error('Error enabling default devices:', error);
        setIsAudioOn(false);
        setIsVideoOn(false);
        setLoading(false);
      }
    };
    enableDefaultDevices();
    return () => {
      localAudio.stop();
      localVideo.stop();
    };
  }, []);

  const toggleAudio = useCallback(async () => {
    try {
      if (isAudioOn) {
        await localAudio.stop();
        setIsAudioOn(false);
      } else {
        await localAudio
          .start()
          .then(() => {
            setIsAudioOn(true);
          })
          .catch(async (err) => {
            if (err.message === 'Permission denied') {
              showToast({ message: 'Enable permission for using audio', variant: 'error' });
              await navigator.mediaDevices.getUserMedia({ audio: true });
            }
          });
      }
    } catch (error) {
      console.error('Error toggling audio:', error);
    }
  }, [isAudioOn]);

  const toggleVideo = useCallback(async () => {
    try {
      if (isVideoOn) {
        await localVideo.stop();
        setIsVideoOn(false);
      } else {
        if (videoRef.current) {
          await localVideo
            .start(videoRef.current)
            .then(() => {
              setIsVideoOn(true);
            })
            .catch(async (err) => {
              if (err.message === 'Permission denied') {
                showToast({ message: 'Enable permission for using video', variant: 'error' });
                await navigator.mediaDevices.getUserMedia({ video: true });
              }
              console.log('err', err);
            });
        }
      }
    } catch (error) {
      console.error('Error toggling video:', error);
    }
  }, [isVideoOn]);

  useEffect(() => {
    const handleDeviceChange = async () => {
      await mountDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  return (
    <div className='w-full h-full flex flex-col md:flex-row items-center justify-center bg-black gap-10'>
      {joinLoading ? (
        <>
          <Loader className='bg-black h-[calc(100vh-73px)]' />
          <h4 className='absolute w-full h-full top-0 flex items-center justify-center pt-20'>
            Joining session
          </h4>
        </>
      ) : (
        <>
          <div className='w-[630px] max-w-[95%]'>
            <div className='space-y-4'>
              <div className='aspect-video bg-muted rounded-lg overflow-hidden relative'>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className='w-full h-full object-cover bg-[#16181B]'
                />
                <div
                  className={cn(
                    'w-full h-full flex items-center justify-center bg-[#212134] absolute top-0 left-0',
                    isVideoOn ? 'hidden' : '',
                  )}
                >
                  <h3 className='font-medium'>
                    {loading ? 'Camera is starting' : 'Camera is off'}
                  </h3>
                </div>
                <div className='flex items-center gap-2 absolute top-4 left-4'>
                  <Avatar className='h-8 w-8'>
                    <AvatarImage src={userDetails?.profileImage || ''} alt='Profile picture' />
                    <AvatarFallback>{userDetails?.displayName?.charAt(0) || ''}</AvatarFallback>
                  </Avatar>
                  <h4 className='font-semibold text-base'>{userDetails?.firstName + ' ' + userDetails?.lastName}</h4>
                </div>
                <div className='flex justify-center items-center gap-4 absolute bottom-4 w-full'>
                  <Button
                    id='mute-btn'
                    variant='outline'
                    size='icon'
                    onClick={toggleAudio}
                    className='bg-white w-12 h-12  hover:bg-[#bebebe]'
                  >
                    {isAudioOn ? (
                      <img src={MicOn} alt='mic on' />
                    ) : (
                      <img src={MicMute} alt='mic off' />
                    )}
                  </Button>
                  <Button
                    id='video-btn'
                    variant='outline'
                    size='icon'
                    onClick={toggleVideo}
                    className='bg-white w-12 h-12  hover:bg-[#bebebe]'
                  >
                    {isVideoOn ? (
                      <img src={Video} alt='video on' />
                    ) : (
                      <img src={VideoOff} alt='video off' />
                    )}
                  </Button>
                </div>
              </div>
            </div>
            <div className='grid grid-cols-3 gap-2 mt-4'>
              <Select onValueChange={handleMicChange} value={mic}>
                <SelectTrigger className='w-full justify-start gap-2 rounded-full device_select_trigger'>
                  <Mic /> <SelectValue placeholder='Select microphone' />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {audioInputDevices?.map((device) => (
                      <SelectItem key={device.value} value={device.value} className='w-[300px]'>
                        {truncate(device.text, { length: 34 })}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Select onValueChange={handleSpeakerChange} value={speaker}>
                <SelectTrigger className='w-full justify-start gap-2 rounded-full device_select_trigger'>
                  <Volume2 /> <SelectValue placeholder='Select speaker' />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {audioOutputDevices?.map((device) => (
                      <SelectItem key={device.value} value={device.value} className='w-[300px]'>
                        {truncate(device.text, { length: 34 })}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Select onValueChange={handleCameraChange} value={camera}>
                <SelectTrigger className='w-full justify-start gap-4 rounded-full device_select_trigger'>
                  <Camera /> <SelectValue className='text-left' placeholder='Select camera' />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {cameraDevices?.map((device) => (
                      <SelectItem key={device.value} value={device.value} className='w-[300px]'>
                        {truncate(device.text, { length: 30 })}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col items-center gap-1 flex-wrap max-w-[400px]'>
              <h2 className='text-2xl font-medium text-white'>
                {sessionData?.trainingType?.title} Session
              </h2>
              <h2 className='text-2xl font-medium text-white'>with</h2>
              <h2 className='text-2xl font-medium text-white'>
                {userDetails?.role === Role.Trainee
                  ? sessionData?.trainerIdUser?.firstName
                  : sessionData?.traineeIdUser?.firstName + ' ' + sessionData?.traineeIdUser?.lastName}
              </h2>
            </div>
            <div className='flex gap-2 justify-center'>
              <h4 className='flex gap-1 text-base font-medium pr-2'>
                <Calendar className='h-6 w-6' />
                {dayjs(sessionData?.sessionStartTime).format('ddd MM/DD/YYYY')}
              </h4>
              <h4 className='flex gap-1 text-base font-medium border-l pl-4'>
                <Clock className='h-6 w-6' />
                {dayjs(sessionData?.sessionStartTime).format('hh:mm')} -{' '}
                {dayjs(sessionData?.sessionEndTime).format('hh:mm A')}
              </h4>
            </div>
            <Button
              id='pre-session-join-btn'
              className='w-full mt-14'
              onClick={() => handleJoin()}
              size={'lg'}
            >
              Join Now
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default PreSession;
